import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';
import { UniversalLink } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers';

import {
  RichText,
  richTextHasContent,
} from 'design-comuni-plone-theme/components/ItaliaTheme/View';
import {
  Alert,
  Spinner,
  Card,
  CardBody,
  Button,
  Icon,
  Badge,
  NotificationManager,
} from 'design-react-kit';
import { viewDate } from 'design-comuni-plone-theme/helpers';
import { getStatusOption } from '@redturtle/volto-io-prenoto/helpers/prenotazione';
import { workflowHistory } from '@redturtle/volto-io-prenoto/actions/workflow';
import ModalSentMessages from '@redturtle/volto-io-prenoto/components/View/AreaPersonaleView/ModalSentMessages';
import {
  PrenotazioneDeleteModal,
  PrenotazioneCardIndirizzoOrario,
} from '@redturtle/volto-io-prenoto/components/View/PrenotazioneView';

const messages = defineMessages({
  show_more: {
    id: 'booking_show_more',
    defaultMessage: 'Vedi altri appuntamenti',
  },
  no_bookings: {
    id: 'no_bookings',
    defaultMessage: 'Non ci sono appuntamenti',
  },
  booking_title: {
    id: 'booking_title',
    defaultMessage: 'Prenotazione appuntamento',
  },
  load_fail: {
    id: 'bookings_load_fail',
    defaultMessage:
      'Non è stato possibile caricare gli appuntamenti. Per favore riprovare più tardi.',
  },
  details: {
    id: 'bookings_details',
    defaultMessage: 'Dettagli',
  },
  hide_details: {
    id: 'bookings_hide_details',
    defaultMessage: 'Nascondi i dettagli',
  },
  description: {
    id: 'bookings_description',
    defaultMessage: 'Descrizione',
  },
  email: {
    id: 'bookings_email',
    defaultMessage: 'E-mail',
  },
  phone: {
    id: 'bookings_phone',
    defaultMessage: 'Telefono',
  },
  code: {
    id: 'bookings_code',
    defaultMessage: 'Codice prenotazione:',
  },
  gate: {
    id: 'booking_gate',
    defaultMessage: 'Postazione',
  },
  indirizzo: {
    id: 'booking_address',
    defaultMessage: 'Indirizzo',
  },
  booking_history: {
    id: 'booking_history',
    defaultMessage: 'Storico della prenotazione',
  },
  booking_cancel: {
    id: 'booking_cancel',
    defaultMessage: 'Cancella prenotazione',
  },
  cosa_serve: {
    id: 'booking_cosa_serve',
    defaultMessage: 'Cosa serve',
  },
  bookingConfirmedMessage: {
    id: 'booking_confirmedMessage',
    defaultMessage: "L'appuntamento è fissato per ",
  },
  bookingPendingMessage: {
    id: 'booking_pendingMessage',
    defaultMessage: "L'appuntamento è stato richiesto per ",
  },
  bookingRefusedMessage: {
    id: 'booking_refusedMessage',
    defaultMessage: "L'appuntamento era fissato per ",
  },
  bookingMessage: {
    id: 'bookingMessage',
    defaultMessage: 'Orario selezionato: ',
  },
  at_time: { id: 'booking_at_time', defaultMessage: 'alle ore' },
  bookingSendType: {
    id: 'booking_sendType',
    defaultMessage: 'Tipologia di prenotazione:',
  },
});

const AppuntamentiCardsWrapper = ({
  bookings = {},
  defaultItemsDispalyed = 5,
  loadBookings,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [showModalDelete, toggleModalDelete] = useState(false);
  const [showModalMessages, toggleModalMessages] = useState(false);
  const [itemDetails, setItemDetails] = useState(null);
  const [focusBooking, setFocusBooking] = useState(null);
  const [focusBookingMessages, setFocusBookingMessages] = useState([]);

  // Messages result
  const allBookings = bookings?.result?.items || [];
  const totalBookings = bookings?.result?.items_total || null;

  // Showing 5 more items
  const [bookingsDispalyed, setBookingsDisplayed] = useState([]);
  const [amountDisplayed, setAmountDisplayed] = useState(defaultItemsDispalyed);
  const history = useSelector((state) => state.bookingHistory.subrequests);

  const deleteBookingResponse = useSelector(
    (state) => state.deleteBookingResponse,
  );

  useEffect(() => {
    if (allBookings.length > 0) {
      setBookingsDisplayed(allBookings.slice(0, defaultItemsDispalyed));
    }
  }, [allBookings]);

  const showMoreItems = () => {
    const newQuantity = amountDisplayed + defaultItemsDispalyed;
    setAmountDisplayed(newQuantity);
    setBookingsDisplayed(allBookings.slice(0, newQuantity));
  };

  useEffect(() => {
    if (bookingsDispalyed.length > 0) {
      const bookingsToLoadHistory = bookingsDispalyed.filter(
        (b) =>
          !history?.[b.booking_url]?.result ||
          !history?.[b.booking_url]?.loading,
      );
      bookingsToLoadHistory.forEach((b) => {
        dispatch(workflowHistory(b.booking_url));
      });
    }
  }, [bookingsDispalyed]);

  const getMessages = (booking) => {
    const booking_history = history?.[booking.booking_url]?.result ?? {};
    return Object.values(booking_history).sort((a, b) => {
      const a_date = new Date(a.time);
      const b_date = new Date(b.time);
      if (a_date > b_date) {
        return -1;
      }
      if (a_date < b_date) {
        return 1;
      }
      return 0;
    });
  };
  return bookingsDispalyed.length > 0 ? (
    <>
      {/* Bookings list */}
      <div className="page-section mb-4 mb-lg-5">
        {bookingsDispalyed.map((booking) => {
          const status = getStatusOption(booking.booking_status);
          const msgs = getMessages(booking);
          return (
            <div key={'modal-' + booking.booking_id}>
              <Card className="shadow-sm mb-4 booking-item no-after">
                <CardBody>
                  <div className="booking-top-infos">
                    <div className="card-date">
                      {booking.booking_status === 'refused'
                        ? intl.formatMessage(messages.bookingRefusedMessage)
                        : booking.booking_status === 'pending'
                          ? intl.formatMessage(messages.bookingPendingMessage)
                          : booking.booking_status === 'confirmed'
                            ? intl.formatMessage(
                                messages.bookingConfirmedMessage,
                              )
                            : intl.formatMessage(messages.bookingMessage)}{' '}
                      <span className="text-capitalize">
                        <strong>
                          {viewDate(
                            intl.locale,
                            booking.booking_date,
                            'dddd DD MMMM YYYY',
                          )}
                        </strong>
                      </span>{' '}
                      {intl.formatMessage(messages.at_time)}{' '}
                      <strong>
                        {viewDate(intl.locale, booking.booking_date, 'HH:mm')}
                      </strong>{' '}
                    </div>

                    <div className="booking-status">
                      <span
                        className={`state-circle bg-${status?.color}`}
                      ></span>
                      {booking.booking_status_label}
                    </div>
                  </div>

                  <div className="booking-main-infos">
                    <div className="booking-type">
                      <span className="label">
                        {intl.formatMessage(messages.bookingSendType)}
                      </span>{' '}
                      {booking.booking_type}
                    </div>
                    <div>
                      {msgs?.length > 0 && (
                        <Button
                          color="link"
                          className="fw-semibold ms-0 px-0 booking-messages"
                          onClick={() => {
                            setFocusBooking(booking);
                            setFocusBookingMessages(msgs);
                            toggleModalMessages(true);
                          }}
                          title={intl.formatMessage(messages.booking_history)}
                        >
                          <Badge color="primary">{msgs.length}</Badge>

                          <Icon
                            color="primary"
                            size="md"
                            icon="it-inbox"
                            title={intl.formatMessage(
                              messages.booking_history,
                            )}
                          />
                        </Button>
                      )}
                    </div>
                    <div className="booking-code">
                      <span className="label">
                        {intl.formatMessage(messages.code)}:
                      </span>{' '}
                      {booking.booking_code}
                    </div>
                  </div>

                  <div className="booking-details">
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        setItemDetails(
                          booking.booking_id === itemDetails
                            ? null
                            : booking.booking_id,
                        );
                      }}
                      color="link"
                      className="p-0"
                    >
                      {itemDetails === booking.booking_id
                        ? intl.formatMessage(messages.hide_details)
                        : intl.formatMessage(messages.details)}
                    </Button>
                    {itemDetails === booking.booking_id && (
                      <>
                        {booking.booking_gate && (
                          <div className="booking-gate">
                            <span className="label">
                              {intl.formatMessage(messages.gate)}:
                            </span>{' '}
                            {booking.booking_gate}
                          </div>
                        )}
                        {booking.description && (
                          <div className="booking-description">
                            <span className="label">
                              {intl.formatMessage(messages.description)}:
                            </span>{' '}
                            {booking.description}
                          </div>
                        )}
                        {booking.email && (
                          <div className="booking-email">
                            <span className="label">
                              {intl.formatMessage(messages.email)}:
                            </span>{' '}
                            {booking.email}
                          </div>
                        )}
                        {booking.phone && (
                          <div className="booking-phone">
                            <span className="label">
                              {intl.formatMessage(messages.phone)}:
                            </span>{' '}
                            {booking.phone}
                          </div>
                        )}
                        {richTextHasContent(booking.cosa_serve) && (
                          <div className="needed my-3">
                            <div className="mb-2 label">
                              {intl.formatMessage(messages.cosa_serve)}:
                            </div>
                            <div
                              className="my-2 bg-primary px-3 py-2"
                              style={{
                                '--bs-bg-opacity': '.05',
                              }}
                            >
                              <RichText data={booking.cosa_serve} />
                            </div>
                          </div>
                        )}
                        {booking.booking_folder && (
                          <div className="address my-3">
                            <div className="label mb-2">
                              {intl.formatMessage(messages.indirizzo)}:
                            </div>
                            <Card className="card-teaser border-left-card rounded shadow p-3 card-small w-auto flex-column my-2">
                              {booking.booking_office ? (
                                <h5 className="card-title fw-semibold">
                                  <UniversalLink
                                    href={flattenToAppURL(
                                      booking.booking_office['@id'],
                                    )}
                                    data-element="service-generic-access"
                                  >
                                    {booking.booking_office.title}
                                  </UniversalLink>
                                </h5>
                              ) : (
                                <h5 className="card-title fw-semibold">
                                  {booking.booking_folder.title}
                                </h5>
                              )}

                              <CardBody className="p-0 pt-3 radio-card--info">
                                <PrenotazioneCardIndirizzoOrario
                                  // passando folder si hanno anche info su orari di apertura,
                                  // ma probabilmente non utili in questo frangente
                                  // folder={booking.booking_folder}
                                  address={
                                    booking.booking_office?.sede?.[0] ||
                                    booking.booking_address
                                  }
                                  contacts={
                                    booking.booking_office?.contact_info
                                  }
                                />
                              </CardBody>
                            </Card>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <div className="actions text-end">
                    <Button
                      color="warning"
                      className="d-print-none"
                      size="xs"
                      disabled={deleteBookingResponse.loading}
                      onClick={() => {
                        setFocusBooking(booking);
                        toggleModalDelete(!showModalDelete);
                      }}
                    >
                      <Icon
                        color="white"
                        className="me-2"
                        size="sm"
                        icon="it-delete"
                      />
                      {intl.formatMessage(messages.booking_cancel)}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </div>
          );
        })}

        {/* SHOW MORE BUTTON */}
        {bookingsDispalyed.length < totalBookings && (
          <Button
            color="link"
            size="xs"
            className="text-decoration-none fw-semibold"
            onClick={showMoreItems}
          >
            {intl.formatMessage(messages.show_more)}
          </Button>
        )}

        {/* MODALE MESSAGGI INVIATI */}
        <ModalSentMessages
          isOpen={showModalMessages}
          toggleModal={toggleModalMessages}
          booking={focusBooking}
          bookingMessages={focusBookingMessages}
        />

        {/* MODALE CANCELLA PRENOTAZIONE */}
        <PrenotazioneDeleteModal
          isOpen={showModalDelete}
          toggleModal={toggleModalDelete}
          booking={focusBooking}
          setLoadDelete={(deleted) => {
            if (deleted) {
              //reload bookings
              loadBookings();
            }
          }}
        />

        {/* TOAST DI NOTIFICA CANCELLAZIONE */}
        <NotificationManager />
      </div>
    </>
  ) : bookings.loading ? (
    <>
      {/* Loading list */}
      <Spinner active />
    </>
  ) : bookings.loaded && allBookings.length === 0 ? (
    <>
      {/* Empty list*/}
      <p>{intl.formatMessage(messages.no_bookings)}</p>
    </>
  ) : !bookings.loading && bookings.error ? (
    <>
      {/* Error from api call list */}
      <Alert color="danger">
        <strong>{intl.formatMessage(messages.load_fail)}</strong>
      </Alert>
    </>
  ) : (
    <></>
  );
};

export default AppuntamentiCardsWrapper;
